import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { combineLatest, map, tap } from "rxjs/operators";
import { ISecureStorage } from "../../classes/interfaces/isecurestorage";
import { withSpinner } from "../../extensions/withSpinner.extension";
import { API_ROOT, SECURE_LOCAL_STORAGE } from "../../types/constants/appconstants";
import { SubGroup } from "../../types/subgroup";
import { OrganizationService } from "./organization.service";

@Injectable({
    "providedIn": "root"
})
export class SubGroupsService {
    private http = inject(HttpClient);
    private apiRoot = inject(API_ROOT);
    private organizationService = inject(OrganizationService);
    private storage = inject<ISecureStorage>(SECURE_LOCAL_STORAGE);
    notifyMarketsChanged = new BehaviorSubject<any>(null);
    private subGroupBaseUrl = this.apiRoot + "api/subgroups";
    private subGroupStorageKey = "SubGroups";

    deleteSubGroup(subgroupID: number) {
        return withSpinner(this.http).delete(this.subGroupBaseUrl + "/" + subgroupID).pipe(
            tap((sg) => this.clearSubGroupsCache())
        );
    }

    getSubGroup(subGroupID: number): Observable<SubGroup> {
        // GET /subgroups/{subGroupId}
        return withSpinner(this.http).get<SubGroup>(this.subGroupBaseUrl + "/" + subGroupID);
    }

    getSubGroups(marketIds: number[], useCache:boolean = true): Observable<SubGroup[]> {
        var cacheSubGroups = this.storage.getItem<SubGroup>(this.subGroupStorageKey) as SubGroup[];
        let hasMarketSubGroupsInCache: boolean = marketIds.every(mid => cacheSubGroups?.some(sg => mid === sg.divisionID));
        if(useCache && cacheSubGroups && hasMarketSubGroupsInCache) {
            const filteredSubGroups = cacheSubGroups.filter(sg => marketIds.some(id => id === sg.divisionID));
            const subgroups$ = of(filteredSubGroups);
            return subgroups$;
        }

        const result$ = withSpinner(this.http).get<SubGroup[]>(this.subGroupBaseUrl + "?divisionIds=[" + marketIds.join(",") + "]").pipe(
            combineLatest(this.organizationService.getAllMarkets()),
            map(([subGroups, mkts]) => {
                subGroups.forEach((subGroup) => {
                    const org = mkts.find((m) => m.orgId === subGroup.divisionID);

                    if (org) {
                        subGroup.divisionName = org.orgName;
                    }

                });
                this.storage.setItem(this.subGroupStorageKey, subGroups);

                return subGroups;
            })
        );

        return result$;
    }

    saveSubGroup(divisionID: number, subgroup: any): Observable<number> {
        if (subgroup.subGroupID === -1) {
            // POST /markets/{marketNumber}/subgroups
            return withSpinner(this.http).post<SubGroup>(this.subGroupBaseUrl + "/markets/" + divisionID + "/subgroups", { "subgroup": subgroup }).pipe(
                tap((sg) => this.clearSubGroupsCache()),
                map((r) => r.subGroupID)
            );
        } else {
            // PUT /markets/{marketNumber}/subgroups/{subgroupID}
            return withSpinner(this.http).put(this.subGroupBaseUrl + "/markets/" + divisionID + "/subgroups/" + subgroup.subGroupID, { "subgroup": subgroup }).pipe(
                tap((sg) => this.clearSubGroupsCache()),
                map((r) => subgroup.subGroupID)
            );
        }
    }

    clearSubGroupsCache(): void {
        this.storage.removeItem(this.subGroupStorageKey);
    }
}